import { TranslationKey } from './../../../../types/translation/TranslationKey';
import React from 'react';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { Tag } from '../../../../types/tag/tag';
import { TagRelation } from '../../../../types/tag/tagRelation';
import { EntityType, entityTypeToString } from '../../../../types/comment';
import { t } from '../../../../types/translation/Translator';
import { toFilterString } from '../../../../util/string.util';
import { BaseFilter, BaseFilterProps } from '../common/BaseFilter';
import { FilterProps } from '../common/filter.util';
import { TagFilterDropdownContent, TagFilterInnerContent } from './DropdownContent';

export class TagFilter extends BaseFilter<Tag> {
  entityType: EntityType | undefined;

  // entityType should only be undefined when creating a tag filter for tags themselves
  constructor(entityType: EntityType | undefined) {
    super();
    this.entityType = entityType;
  }

  toLabel(): string {
    if (!this.entityType) return t(TranslationKey.tag).plural.upper;
    return `${t(TranslationKey.tag).plural.label} (${entityTypeToString(this.entityType)})`;
  }

  toIcon(): React.JSX.Element {
    return <LocalOfferOutlinedIcon sx={{ width: '15px', height: '15px' }} />;
  }

  toDropdownContent(props: BaseFilterProps): React.JSX.Element {
    if (!this.entityType) return <></>;
    return (
      <TagFilterDropdownContent
        entityType={this.entityType}
        filter={this}
        filterProps={props.filterProps}
        setFilterProps={props.setFilterProps}
      />
    );
  }

  toInnerContent(props: BaseFilterProps): string | JSX.Element {
    if (!this.entityType) return <></>;
    return (
      <TagFilterInnerContent
        entityType={this.entityType}
        filter={this}
        filterProps={props.filterProps}
        setFilterProps={props.setFilterProps}
      />
    );
  }

  toCount(props: BaseFilterProps): number {
    if (!this.entityType) return 0;
    return props.filterProps.tags?.get(this.entityType)?.size || 0;
  }

  search(item: Tag, textFilter: string) {
    const textFilterString = toFilterString(textFilter);

    if (toFilterString(item.name).includes(textFilterString)) {
      return true;
    }

    return false;
  }

  /**
   * @returns {Set<string>}
   * - Set containing ids of allowed entities if any permitted by tagRelations.
   * - Empty Set if:
   *    - No Tags (TagRelations) set in FilterProps
   *    - Entity provided is not limited by Tags logic (tag filtering not applicable)
   */
  static filter(tagRelations: Map<string, TagRelation>, entityType: EntityType, filterProps: FilterProps): Set<string> {
    if (!filterProps.tags?.size) return new Set();
    const forEntityType = filterProps.tags.get(entityType);
    if (!forEntityType?.size) return new Set();

    return new Set(
      [...tagRelations.values()]
        .filter(tr => tr.type === entityType && forEntityType.has(tr.tagId))
        .map(tr => tr.entityId),
    );
  }

  static filterBy(
    filterProps: FilterProps,
    entityType: EntityType,
    tagRelations: Map<string, TagRelation>,
    id: string | undefined,
  ): boolean {
    if (!filterProps.tags?.size) return true;
    const forEntityType = filterProps.tags.get(entityType);
    if (!forEntityType?.size) return true;

    return this.filter(tagRelations, entityType, filterProps).has(id || '');
  }
}
